import React, { Component } from 'react';
import { Formik } from 'formik';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import uniqBy from 'lodash/uniqBy';
import { Alert, AlertLink, Link, Select, SubmitButton } from 'localmed-core';

import style from './AppointmentForm.scss';

import LegacyPanel from '../../../legacyCore/LegacyPanel';
import Form, {
  Checkbox,
  Fieldset,
  Field,
  FormContent,
  Input,
  FieldHint,
} from '../../../legacyCore/Form';
import { DateInput } from '../../../legacyCore/MaskedInput';
import PasswordInput from '../../../legacyCore/PasswordInput';
import { Scaleup } from '../../../legacyCore/Transitions';
import {
  APPOINTMENT_STATUS,
  PATIENT_STATUS,
  CASH_PAYMENT_ID,
  OTHER_ID,
  FLASH_MESSAGES,
} from '../../constants';
import { trackAffiliateEvent } from '../../utils/affiliateTracking';
import {
  buildAppointmentUrl,
  buildProviderSchedulingUrl,
  getQueryFromLocation,
  updateParams,
} from '../../utils/urls';

import CheckmarkList from '../CheckmarkList';
import EmailInput from '../EmailInput';
import InsuranceIssuerSelect from '../InsuranceIssuerSelect';
import LoginLink from '../LoginLink';
import LogoutLink from '../LogoutLink';
import ReasonForVisitSelect from '../ReasonForVisitSelect';
import AppointmentFormInfo from './AppointmentFormInfo';
import { DEPENDENT_STATUS } from './constants';
import getInitialValues from './getInitialValues';
import { deleteNewUserSession, getNewUserSession, saveNewUserSession } from './newUserSession';
import OpeningNotAvailable from './OpeningNotAvailable';
import submitAppointment from './submitAppointment';
import TermsAgreementMessage from './TermsAgreementMessage';

import { combineErrors, isPatientCoverageInfoRequired } from './utils';
import validateAppointment from './validateAppointment';
import { withCustomContexts } from '../Contexts';

class AppointmentForm extends Component {
  submitCount = 0;
  justSubmitted = false;
  wasSubmitting = false;
  state = {
    showBenefits: false,
  };

  UNSAFE_componentWillMount() {
    const { provider, universalContext, widgetSettings } = this.props;
    const eventProperties = {
      providerId: provider.id,
      providerDisplay: provider.display,
      officeId: provider.office.id,
      officeDisplay: provider.office.display,
    };
    universalContext.trackEvent({
      onServer: true,
      onClient: false,
      event: 'Viewed Opening',
      properties: eventProperties,
    });
    universalContext.trackEvent({
      event: 'Viewed Appointment Form Version 2',
      properties: eventProperties,
    });
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      event: 'Viewed LocalMed Appointment Form',
      properties: eventProperties,
    });
  }

  onSubmit = async (values, form) => {
    const response = await submitAppointment(values, this.props);
    const { appointment } = response;

    form.setSubmitting(false);
    this.trackEvents(response, values);
    form.setFieldValue('user', response.user);
    form.setFieldValue('patient', response.patient);
    form.setFieldValue('serverErrors', response.errors);
    form.setStatus(response);

    if (response.isSuccessful) {
      if (values.createAccount) {
        saveNewUserSession(response);
      }

      if (appointment && appointment.status === APPOINTMENT_STATUS.PENDING) {
        this.redirectToAppointment(response, values);
      } else {
        this.props.showVerification(values.contactEmail);
      }
    }
  };

  getFormError(form) {
    if (this.submitCount === 0 || form.isSubmitting) {
      return null;
    }
    const { status = {}, isValid } = form;
    const unhandledServerErrors = omit(status.errors || {}, [
      'username',
      'opening',
      'contactPhone',
      'patientCoverage.insurancePhone',
      'patientCoverage.postalCode',
      'patientCoverage.ssn',
    ]);
    const hasUnhandledServerErrors =
      status.errors == null || Object.keys(unhandledServerErrors).length > 0;
    if (status.isSuccessful === false && hasUnhandledServerErrors) {
      // If there are any fields we don't specifically handle in the UI, we’ll display
      // the generic error message to the user.
      return 'We’re sorry, the appointment could not be scheduled because of an error. We’ve been notified and are working to correct the issue. Please try again in a few minutes.';
    }
    if (!isValid) {
      return 'Please correct the errors in the form and submit again.';
    }
    return null;
  }

  getInitialValues() {
    const {
      location,
      provider,
      opening,
      widgetSettings,
      prefillToken,
      trackerData,
      scheduleRequestAttemptData,
      isOnWidget,
    } = this.props;
    const query = getQueryFromLocation(location);
    return getInitialValues({
      currentUser: this.getCurrentUser(),
      provider,
      opening,
      query,
      widgetSettings,
      prefillToken,
      trackerData,
      scheduleRequestAttemptData,
      isOnWidget,
    });
  }

  getCurrentUser() {
    return this.props.currentUser || getNewUserSession();
  }

  updateFormSubmissionState(form) {
    this.justSubmitted = this.wasSubmitting && !form.isSubmitting;
    if (this.justSubmitted) {
      this.submitCount += 1;
    }
    this.wasSubmitting = form.isSubmitting;
    if (this.justSubmitted && form.errors && Object.keys(form.errors).length > 0) {
      this.props.universalContext.trackEvent({
        event: 'Submitted Invalid Appointment',
        properties: { errors: form.errors, submitCount: this.submitCount },
      });
    }
  }

  validate = (values) => validateAppointment(values, this.props.provider);

  trackEvents(response, values) {
    const { universalContext, widgetSettings } = this.props;
    const submittedAppointmentEvent = {
      event: 'Submitted Appointment',
      properties: {
        ...pick(response, ['isSuccessful', 'status', 'message', 'errors']),
        submitCount: this.submitCount,
      },
    };
    universalContext.trackEvent(submittedAppointmentEvent);
    if (!response.isSuccessful || response.appointment == null) {
      return;
    }
    const { provider } = this.props;
    const {
      contactMobilePhone,
      reasonForVisit,
      insuranceIssuer,
      dependentStatus,
      createAccount,
    } = values;
    const isForDependent = dependentStatus === DEPENDENT_STATUS.DEPENDENT;
    const scheduledAppointmentEvent = {
      event: 'Scheduled Appointment',
      properties: {
        contactMobilePhone,
        appointmentId: response.appointment.id,
        providerId: provider.id,
        providerDisplay: provider.display,
        officeId: provider.office.id,
        officeDisplay: provider.office.display,
        practiceId: provider.practice.id,
        practiceDisplay: provider.practice.display,
        reasonForVisitId: reasonForVisit.id,
        reasonForVisitDisplay: reasonForVisit.display,
        patientStatusId: reasonForVisit.patientStatus.toLowerCase(),
        insuranceIssuerId:
          insuranceIssuer && insuranceIssuer.id !== OTHER_ID ? insuranceIssuer.id : null,
        insuranceIssuerDisplay:
          insuranceIssuer && insuranceIssuer.id !== OTHER_ID ? insuranceIssuer.display : null,
        createdAccount: createAccount,
        createdDependent: createAccount && isForDependent,
        scheduledForDependent: isForDependent,
        submitCount: this.submitCount,
      },
    };
    universalContext.trackEvent(scheduledAppointmentEvent);
    trackAffiliateEvent({
      aftId: widgetSettings.aftId,
      aftType: widgetSettings.aftType,
      aft: widgetSettings.aft,
      ...scheduledAppointmentEvent,
    });
  }

  redirectToAppointment(status, values) {
    const { provider, history, useMainLayout } = this.props;
    const { appointment } = status;
    const { insuranceIssuer } = values;
    // Setup some params for tracking in Hotjar/Google Analytics
    let paymentMethod;
    let insuranceIssuerId;
    if (insuranceIssuer == null || insuranceIssuer.id === OTHER_ID) {
      paymentMethod = 'Other';
    } else if (insuranceIssuer.id === CASH_PAYMENT_ID) {
      paymentMethod = 'Cash';
    } else {
      paymentMethod = 'Insurance';
      insuranceIssuerId = insuranceIssuer.id;
    }

    const appointmentUrl = buildAppointmentUrl(provider.slug, appointment.id, {
      patientToken: appointment.patientToken,
      params: {
        paymentMethod,
        insuranceIssuer: insuranceIssuerId,
        message: appointment.status === APPOINTMENT_STATUS.PENDING ? FLASH_MESSAGES.SCHEDULED : '',
      },
      useMainLayout,
    });
    history.push(appointmentUrl);
  }

  renderPatientCoverageFields(form) {
    return (
      <Fieldset legend="Insurance Info">
        <Field name="insurancePhone" label="Insurance Phone Number">
          <Input
            type="tel"
            name="insurancePhone"
            placeholder="(###) ###-####"
            autoComplete="off"
            onBlur={() => {
              form.setFieldTouched('insurancePhone');
              form.setFieldValue('serverErrors', {
                ...form.values.serverErrors,
                'patientCoverage.insurancePhone': null,
              });
            }}
          />
          <FieldHint>
            This is found on your insurance card. If there are multiple numbers, enter the{' '}
            <em>Customer Support</em> number.
          </FieldHint>
        </Field>
        <Field name="memberId" label="Member ID">
          <Input name="memberId" />
        </Field>
        <Field name="group" label="Group Number">
          <Input name="group" />
        </Field>
        <Field name="employer" label="Employer Name">
          <Input name="employer" />
        </Field>
        <Field
          htmlFor="subscriberGivenName"
          label="Policy Holder’s Name"
          errors={combineErrors(form, ['subscriberGivenName', 'subscriberFamilyName'])}
        >
          <div className={style['appointment-form__name-row']}>
            <div className={style['appointment-form__name-column']}>
              <Input name="subscriberGivenName" placeholder="First" autoComplete="given-name" />
            </div>
            <div className={style['appointment-form__name-column']}>
              <Input name="subscriberFamilyName" placeholder="Last" autoComplete="family-name" />
            </div>
          </div>
        </Field>
        <Field name="subscriberBirthDate" label="Policy Holder’s Birthday">
          <DateInput name="subscriberBirthDate" autoComplete="bday" />
          <FieldHint>Format: MM/DD/YYYY</FieldHint>
        </Field>
        <Field name="subscriberPostalCode" label="Policy Holder’s Postal Code">
          <Input
            name="subscriberPostalCode"
            mask="11111"
            placeholder="#####"
            onBlur={() => {
              form.setFieldTouched('subscriberPostalCode');
              form.setFieldValue('serverErrors', {
                ...form.values.serverErrors,
                'patientCoverage.subscriberPostalCode': null,
              });
            }}
          />
        </Field>
        <Field name="subscriberSsn" label="Policy Holder’s SSN">
          <Input
            name="subscriberSsn"
            mask="111-11-1111"
            placeholder="###-##-####"
            onBlur={() => {
              form.setFieldTouched('subscriberSsn');
              form.setFieldValue('serverErrors', {
                ...form.values.serverErrors,
                'patientCoverage.ssn': null,
              });
            }}
          />
        </Field>
      </Fieldset>
    );
  }

  renderAnonymousFields(form) {
    const { values } = form;
    const isForDependent = values.dependentStatus === DEPENDENT_STATUS.DEPENDENT;
    return (
      <React.Fragment>
        <Fieldset legend="Patient Info">
          <Field name="dependentStatus">
            <p>I am scheduling this appointment for:</p>
            <ul className="list-reset">
              <li>
                <Checkbox
                  type="radio"
                  id="dependentStatusMe"
                  name="dependentStatus"
                  value={DEPENDENT_STATUS.ME}
                >
                  Myself
                </Checkbox>
              </li>
              <li>
                <Checkbox
                  type="radio"
                  id="dependentStatusDependent"
                  name="dependentStatus"
                  value={DEPENDENT_STATUS.DEPENDENT}
                  onChange={(event) => {
                    form.setTouched({
                      ...form.touched,
                      contactGivenName: false,
                      contactFamilyName: false,
                      contactBirthDate: false,
                    });
                    form.handleChange(event);
                  }}
                >
                  My child or Spouse
                </Checkbox>
              </li>
            </ul>
          </Field>
          <Field
            htmlFor="patientGivenName"
            label={isForDependent ? 'Their Name' : 'Your Name'}
            errors={combineErrors(form, ['patientGivenName', 'patientFamilyName'])}
          >
            <div className={style['appointment-form__name-row']}>
              <div className={style['appointment-form__name-column']}>
                <Input
                  name="patientGivenName"
                  placeholder="First"
                  autoComplete={isForDependent ? 'off' : 'given-name'}
                />
              </div>
              <div className={style['appointment-form__name-column']}>
                <Input
                  name="patientFamilyName"
                  placeholder="Last"
                  autoComplete={isForDependent ? 'off' : 'family-name'}
                />
              </div>
            </div>
          </Field>
          <Field
            name="patientBirthDate"
            label={isForDependent ? 'Their Birthday' : 'Your Birthday'}
          >
            <DateInput name="patientBirthDate" autoComplete={isForDependent ? 'bday' : 'off'} />
            <FieldHint>Format: MM/DD/YYYY</FieldHint>
          </Field>
        </Fieldset>
        <Fieldset legend="Contact Info">
          {isForDependent && (
            <Scaleup key="CONTACT_INFO" in>
              <div className="margin-bottom-sm">
                <Field
                  htmlFor="contactGivenName"
                  label="Your Name"
                  errors={combineErrors(form, ['contactGivenName', 'contactFamilyName'])}
                >
                  <div className={style['appointment-form__name-row']}>
                    <div className={style['appointment-form__name-column']}>
                      <Input
                        name="contactGivenName"
                        placeholder="First"
                        autoComplete="given-name"
                      />
                    </div>
                    <div className={style['appointment-form__name-column']}>
                      <Input
                        name="contactFamilyName"
                        placeholder="Last"
                        autoComplete="family-name"
                      />
                    </div>
                  </div>
                </Field>
                <Field name="contactBirthDate" label="Your Birthday">
                  <DateInput name="contactBirthDate" autoComplete="bday" />
                  <FieldHint>Format: MM/DD/YYYY</FieldHint>
                </Field>
              </div>
            </Scaleup>
          )}
          <Field name="contactEmail" label="Email">
            <EmailInput
              name="contactEmail"
              autoComplete="email"
              validationResult={values.emailValidationResult}
              onValidate={(validationResult) =>
                form.setFieldValue('emailValidationResult', validationResult)
              }
              onClickDidYouMean={(newEmail) => form.setFieldValue('contactEmail', newEmail)}
            />
          </Field>
          <Field name="contactMobilePhone" label="Mobile Phone">
            <Input
              type="tel"
              name="contactMobilePhone"
              placeholder="(###) ###-####"
              autoComplete="tel"
              onBlur={() => {
                form.setFieldTouched('contactMobilePhone');
                form.setFieldValue('serverErrors', {
                  ...values.serverErrors,
                  contactPhone: null,
                });
              }}
            />
          </Field>
        </Fieldset>
        {this.renderCreateAccountFields(form)}
      </React.Fragment>
    );
  }

  renderCreateAccountFields({ values, ...form }) {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      return null;
    }
    return (
      <Fieldset legend="Account Info">
        <Field name="createAccount">
          <Checkbox
            name="createAccount"
            onChange={(event) => {
              if (values.username === '') {
                form.setFieldValue('username', values.contactEmail);
              }
              form.setFieldTouched('password', false);
              form.handleChange(event);
            }}
          >
            I would like to create a LocalMed&nbsp;Account.
          </Checkbox>
          <Link
            id="benefitsToggle"
            marginLeft="md"
            onClick={() =>
              this.setState(({ showBenefits }) => ({
                showBenefits: !showBenefits,
              }))
            }
          >
            Benefits of making an account
          </Link>
          {this.state.showBenefits && (
            <Scaleup key="BENEFITS_LIST" in>
              <LegacyPanel
                id="benefitsList"
                className="margin-top-sm margin-bottom-none"
                color="gray"
              >
                <CheckmarkList>
                  <li>Enter information once and we’ll save it for future appointments.</li>
                  <li>Schedule and manage appointments for your dependents under one profile.</li>
                  <li>Store current dentists in your profile for easy recall.</li>
                  <li>Keep track of all your appointments scheduled with LocalMed.</li>
                </CheckmarkList>
              </LegacyPanel>
            </Scaleup>
          )}
        </Field>
        {values.createAccount && (
          <Scaleup key="ACCOUNT_INFO" in>
            <div>
              <Field name="username" label="Login Email">
                <Input
                  type="email"
                  name="username"
                  onBlur={() => {
                    form.setFieldTouched('username');
                    form.setFieldValue('serverErrors', { ...values.serverErrors, username: null });
                  }}
                />
              </Field>
              <Field name="password" label="Password">
                <PasswordInput name="password" />
              </Field>
            </div>
          </Scaleup>
        )}
      </Fieldset>
    );
  }

  renderSignedInFields({ values, ...form }) {
    const currentUser = this.getCurrentUser();
    if (currentUser.patient == null) {
      throw new Error(
        'Cannot render signed in fields because the `user` does not have a `patient`'
      );
    }
    const patients = [currentUser.patient, ...currentUser.patient.dependents.nodes];
    const meOption = { id: currentUser.patient.id, display: `Me (${currentUser.patient.display})` };
    const createDependentOption = { id: '', display: 'Add a dependent' };
    const patientOptions = [
      meOption,
      ...currentUser.patient.dependents.nodes,
      createDependentOption,
    ];
    return (
      <Fieldset legend="Patient Info">
        <Field
          name="patient"
          htmlFor="patient-input"
          label="Patient"
          errors={combineErrors(form, ['patient', 'patient.birthDate'])}
        >
          <Select
            id="patient"
            name="patient"
            placeholder="Select a patient…"
            gray
            items={patientOptions}
            selectedItem={values._patientDisplay}
            onChange={(value) => {
              form.setFieldValue('patient', patients[patientOptions.indexOf(value)]);
              form.setFieldValue('_patientDisplay', value);
              form.setFieldValue('createDependent', value === createDependentOption);
            }}
            onClose={() => form.setFieldTouched('patient')}
          />
        </Field>
        {values.createDependent && (
          <Scaleup key="DEPENDENT_INFO" in>
            <div>
              <Field
                htmlFor="patientGivenName"
                label="Their Name"
                errors={combineErrors(form, ['patientGivenName', 'patientFamilyName'])}
              >
                <div className={style['appointment-form__name-row']}>
                  <div className={style['appointment-form__name-column']}>
                    <Input name="patientGivenName" placeholder="First" autoComplete="off" />
                  </div>
                  <div className={style['appointment-form__name-column']}>
                    <Input name="patientFamilyName" placeholder="Last" autoComplete="off" />
                  </div>
                </div>
              </Field>
              <Field name="patientBirthDate" label="Their Birthday">
                <DateInput name="patientBirthDate" autoComplete="off" />
                <FieldHint>Format: MM/DD/YYYY</FieldHint>
              </Field>
            </div>
          </Scaleup>
        )}
      </Fieldset>
    );
  }

  render() {
    const { location, history, provider, opening, isOnWidget } = this.props;
    const query = getQueryFromLocation(location);
    const currentUser = this.getCurrentUser();
    const signedIn = currentUser != null;
    const hasPatient = signedIn && currentUser.patient != null;

    return (
      <Formik
        initialValues={this.getInitialValues()}
        validate={this.validate}
        onSubmit={this.onSubmit}
      >
        {(form) => {
          this.updateFormSubmissionState(form);
          const error = this.getFormError(form);
          const { values, status } = form;
          const isForDependent = values.dependentStatus === DEPENDENT_STATUS.DEPENDENT;
          const openingReasonsForVisit = opening.reasonsForVisit.nodes;
          let reasonsForVisit = uniqBy(openingReasonsForVisit, (r) => r.id);
          if (values.patientStatus) {
            reasonsForVisit = reasonsForVisit.filter(
              (reason) => reason.patientStatus === values.patientStatus
            );
          }
          return (
            <Form horizontal gray>
              <Fieldset>
                {error && (
                  <Alert tier="error" scrollIntoView marginBottom="sm">
                    {error}
                  </Alert>
                )}
                <FormContent>
                  {signedIn ? (
                    <LogoutLink onClick={deleteNewUserSession}>
                      Not <strong>{currentUser.display}?</strong> Logout.
                    </LogoutLink>
                  ) : (
                    <LoginLink>Already have a LocalMed account? Login.</LoginLink>
                  )}
                </FormContent>
              </Fieldset>
              <Fieldset legend="Appointment Info">
                <Field name="patientStatus">
                  <p>
                    {isForDependent ? 'Has the patient' : 'Have you'} visited this office before?
                  </p>
                  <ul className="list-reset">
                    <li>
                      <Checkbox
                        type="radio"
                        id="patientStatusNew"
                        name="patientStatus"
                        value={PATIENT_STATUS.NEW}
                        onChange={(event) => {
                          form.setFieldValue('reasonForVisit', null);
                          form.setFieldTouched('reasonForVisit', false);
                          form.handleChange(event);
                        }}
                      >
                        No, {isForDependent ? 'they’re' : 'I’m'} a new patient.
                      </Checkbox>
                    </li>
                    <li>
                      <Checkbox
                        type="radio"
                        id="patientStatusReturning"
                        name="patientStatus"
                        value={PATIENT_STATUS.RETURNING}
                        onChange={(event) => {
                          form.setFieldValue('reasonForVisit', null);
                          form.setFieldTouched('reasonForVisit', false);
                          form.handleChange(event);
                        }}
                      >
                        Yes, {isForDependent ? 'they' : 'I'} have visited this office before.
                      </Checkbox>
                    </li>
                  </ul>
                  {reasonsForVisit.length === 0 && (
                    <Scaleup key="NO_REASONS" in>
                      <Alert tier="warning">
                        This appointment is only for{' '}
                        {values.patientStatus === PATIENT_STATUS.NEW ? 'returning' : 'new'}{' '}
                        patients.
                        <br />
                        Please{' '}
                        <AlertLink
                          href={buildProviderSchedulingUrl(provider.id, {
                            location,
                            widget: true,
                            params: {
                              reasonForVisitId: '',
                              patientStatusId: values.patientStatus.toLowerCase(),
                            },
                          })}
                        >
                          select a different appointment.
                        </AlertLink>
                      </Alert>
                    </Scaleup>
                  )}
                </Field>
                <Field
                  name="reasonForVisit"
                  htmlFor="reasonForVisit-input"
                  label="Reason for Visit"
                >
                  <ReasonForVisitSelect
                    id="reasonForVisit"
                    gray
                    items={reasonsForVisit}
                    selectedItem={values.reasonForVisit}
                    onChange={(value) => {
                      form.setFieldValue('reasonForVisit', value);
                      updateParams(
                        history,
                        { reason_for_visit_id: value && value.id },
                        { replace: true }
                      );
                    }}
                    onClose={() => form.setFieldTouched('reasonForVisit')}
                  />
                </Field>
                {provider.office.insuranceCoverageEnabled && (
                  <Field name="insuranceIssuer" htmlFor="insuranceIssuer-input" label="Payment">
                    <InsuranceIssuerSelect
                      id="insuranceIssuer"
                      emptyLabel="Other"
                      gray
                      providerId={provider.id}
                      selectedItem={values.insuranceIssuer}
                      initialSelectedValue={query.insurance_issuer_id || CASH_PAYMENT_ID}
                      onChange={(value) => {
                        form.setFieldValue('insuranceIssuer', value);
                        updateParams(
                          history,
                          { insurance_issuer_id: value && value.id },
                          { replace: true }
                        );
                      }}
                      onClose={() => form.setFieldTouched('insuranceIssuer')}
                      onProviderInsuranceCoverageChange={(value) =>
                        form.setFieldValue('providerInsuranceCoverage', value)
                      }
                    />
                  </Field>
                )}
              </Fieldset>
              {isPatientCoverageInfoRequired(provider, values.insuranceIssuer) && (
                <Scaleup key="INSURANCE_INFO" in>
                  {this.renderPatientCoverageFields(form)}
                </Scaleup>
              )}
              {hasPatient ? this.renderSignedInFields(form) : this.renderAnonymousFields(form)}
              <Fieldset
                legend={
                  <React.Fragment>
                    Message for the Office <small>(optional)</small>
                  </React.Fragment>
                }
              >
                <Field name="notes">
                  <p>Please write any notes or comments for this office below:</p>
                  <Input name="notes" multiline rows="3" />
                </Field>
              </Fieldset>
              <Fieldset>
                <FormContent className={style['appointment-form__info']}>
                  <LegacyPanel color="gray" px={0}>
                    <AppointmentFormInfo provider={provider} opening={opening} />
                  </LegacyPanel>
                </FormContent>
                {!hasPatient && (
                  <Field name="termsAgreement">
                    <Checkbox name="termsAgreement">
                      <TermsAgreementMessage countryCode={provider.office.countryCode} />
                    </Checkbox>
                  </Field>
                )}
                <Field>
                  <SubmitButton
                    tier="alert"
                    submitting={form.isSubmitting || (form.status && form.status.isSuccessful)}
                    defaultText="Schedule Appointment"
                    submittingText="Scheduling"
                    full
                  />
                  {status && status.errors && status.errors.opening && (
                    // TODO: Add support for selecting a new opening without redirecting to the
                    // provider page.
                    <OpeningNotAvailable
                      location={location}
                      provider={provider}
                      isOnWidget={isOnWidget}
                    />
                  )}
                </Field>
              </Fieldset>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

export default withCustomContexts(AppointmentForm);
